<script setup>
/**
 * HolidaysView
 * Full calendar view for holidays/events
 */
import { ref, onMounted, computed } from 'vue'
import { storeToRefs } from 'pinia'
import AppButton from '@/ui/components/AppButton.vue'
import HolidayEditModal from '@/ui/components/holidays/HolidayEditModal.vue'
import {
  useHolidays,
  formatDateKey,
  formatDateDisplay,
  DAY_NAMES,
} from '@/core/composables/useHolidays'
import { useAuthStore } from '@/app/store/auth'
import { useToast } from '@/core/composables/useToast'

const authStore = useAuthStore()
const { success, error: toastError } = useToast()
const {
  holidays,
  loading,
  weeks,
  monthYearDisplay,
  loadHolidays,
  prevMonth,
  nextMonth,
  goToToday,
  getEvent,
  isToday,
} = useHolidays()

// Check if user is admin
const { isAdmin } = storeToRefs(authStore)

// Edit modal state
const showEditModal = ref(false)
const selectedDate = ref(null)
const selectedDateKey = ref('')
const selectedEventText = ref('')

// Handle cell click
function handleCellClick(day) {
  if (!isAdmin.value) return

  selectedDate.value = day.date
  selectedDateKey.value = formatDateKey(day.date)
  selectedEventText.value = getEvent(selectedDateKey.value) || ''
  showEditModal.value = true
}

// Handle save from modal
function handleSaved() {
  showEditModal.value = false
  success('האירוע נשמר בהצלחה')
}

// Handle delete from modal
function handleDeleted() {
  showEditModal.value = false
  success('האירוע נמחק בהצלחה')
}

// Load holidays on mount
onMounted(() => {
  loadHolidays().catch(() => {
    toastError('שגיאה בטעינת האירועים')
  })
})
</script>

<template>
  <div class="holidays-view">
    <!-- Header -->
    <div class="view-header">
      <h1 class="view-title">לוח חגים ואירועים</h1>
    </div>

    <!-- Month Navigation -->
    <div class="month-nav">
      <AppButton variant="secondary" size="sm" @click="prevMonth">
        <svg
          viewBox="0 0 24 24"
          width="16"
          height="16"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <polyline points="9 18 15 12 9 6" />
        </svg>
        חודש קודם
      </AppButton>

      <div class="month-title">
        <span>{{ monthYearDisplay }}</span>
        <button class="today-btn" @click="goToToday">היום</button>
      </div>

      <AppButton variant="secondary" size="sm" @click="nextMonth">
        חודש הבא
        <svg
          viewBox="0 0 24 24"
          width="16"
          height="16"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <polyline points="15 18 9 12 15 6" />
        </svg>
      </AppButton>
    </div>

    <!-- Calendar Grid -->
    <div class="calendar-container" :class="{ loading }">
      <div v-if="loading" class="loading-overlay">
        <div class="spinner"></div>
      </div>

      <table class="calendar-table">
        <thead>
          <tr>
            <th v-for="(dayName, index) in DAY_NAMES" :key="index">{{ dayName }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(week, weekIndex) in weeks" :key="weekIndex">
            <td
              v-for="(day, dayIndex) in week"
              :key="dayIndex"
              class="calendar-cell"
              :class="{
                'other-month': day.isOtherMonth,
                'is-today': isToday(day.date),
                'has-event': getEvent(formatDateKey(day.date)),
                clickable: isAdmin,
              }"
              @click="handleCellClick(day)"
            >
              <div class="cell-content">
                <span class="date-label">{{ formatDateDisplay(day.date) }}</span>
                <span v-if="getEvent(formatDateKey(day.date))" class="event-text">
                  {{ getEvent(formatDateKey(day.date)) }}
                </span>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Admin Hint -->
    <p v-if="isAdmin" class="edit-hint">
      <svg
        viewBox="0 0 24 24"
        width="14"
        height="14"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
      >
        <circle cx="12" cy="12" r="10" />
        <line x1="12" y1="16" x2="12" y2="12" />
        <line x1="12" y1="8" x2="12.01" y2="8" />
      </svg>
      לחץ על תאריך כדי להוסיף או לערוך אירוע
    </p>

    <!-- Edit Modal -->
    <HolidayEditModal
      v-if="showEditModal"
      :date="selectedDate"
      :date-key="selectedDateKey"
      :initial-text="selectedEventText"
      @close="showEditModal = false"
      @saved="handleSaved"
      @deleted="handleDeleted"
    />
  </div>
</template>

<style scoped>
.holidays-view {
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: var(--spacing-md);
  max-width: 1000px;
  margin: 0 auto;
}

/* Header */
.view-header {
  text-align: center;
  margin-bottom: var(--spacing-md);
}

.view-title {
  font-size: var(--font-size-2xl);
  font-weight: var(--font-weight-bold);
  color: var(--text-primary);
  margin: 0;
}

/* Month Navigation */
.month-nav {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--spacing-md);
  background: var(--bg-secondary);
  border-radius: var(--border-radius-lg);
  margin-bottom: var(--spacing-md);
}

.month-title {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
}

.month-title span {
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-bold);
  color: var(--text-primary);
}

.today-btn {
  padding: var(--spacing-xs) var(--spacing-sm);
  background: var(--color-primary);
  border: none;
  border-radius: var(--border-radius-sm);
  color: white;
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-medium);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.today-btn:hover {
  background: var(--color-primary-dark);
}

/* Calendar */
.calendar-container {
  flex: 1;
  overflow: auto;
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-lg);
  position: relative;
}

.calendar-container.loading {
  opacity: 0.7;
  pointer-events: none;
}

.loading-overlay {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 10;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 3px solid var(--border-color);
  border-top-color: var(--color-primary);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.calendar-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
}

.calendar-table th {
  padding: var(--spacing-sm);
  background: var(--bg-secondary);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--text-secondary);
  border-bottom: 1px solid var(--border-color);
}

.calendar-cell {
  vertical-align: top;
  border: 1px solid var(--border-color);
  height: 80px;
  transition: all var(--transition-fast);
}

.calendar-cell.clickable {
  cursor: pointer;
}

.calendar-cell.clickable:hover {
  background: var(--bg-secondary);
}

.calendar-cell.other-month {
  background: var(--bg-tertiary);
  opacity: 0.5;
}

.calendar-cell.is-today {
  background: rgba(99, 102, 241, 0.1);
  border-color: var(--color-primary);
}

.calendar-cell.is-today .date-label {
  background: var(--color-primary);
  color: white;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.cell-content {
  padding: var(--spacing-xs);
  display: flex;
  flex-direction: column;
  gap: 4px;
  height: 100%;
}

.date-label {
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-semibold);
  color: var(--text-secondary);
}

.event-text {
  font-size: var(--font-size-xs);
  color: var(--text-primary);
  background: rgba(245, 158, 11, 0.2);
  padding: 2px 6px;
  border-radius: var(--border-radius-sm);
  word-break: break-word;
  line-height: 1.3;
}

/* Edit Hint */
.edit-hint {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-xs);
  margin-top: var(--spacing-md);
  font-size: var(--font-size-sm);
  color: var(--text-muted);
  font-style: italic;
}

/* Responsive */
@media (max-width: 768px) {
  .holidays-view {
    padding: var(--spacing-sm);
  }

  .month-nav {
    flex-direction: column;
    gap: var(--spacing-sm);
  }

  .calendar-cell {
    height: 60px;
  }

  .event-text {
    font-size: 10px;
  }
}
</style>
